/**
 * Elegir una contraseña nueva, llegando desde el enlace del correo.
 *
 * El enlace trae un `token` de un solo uso que caduca en una hora. Si llega
 * caducado o ya usado, el servidor redirige aquí con `error` en vez de `token`,
 * y la pantalla lo dice claro y ofrece pedir otro: dejar a alguien delante de un
 * formulario que va a fallar al enviarlo es hacerle perder el tiempo dos veces.
 *
 * Al cambiarla se cierran todas las sesiones abiertas de esa cuenta. Quien
 * recupera la contraseña suele hacerlo porque sospecha que alguien más la tiene.
 */

import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthLayout, Button, Card, ErrorMessage, Field, Notice, PasswordInput } from '../components/ui.tsx';
import { authClient } from '../lib/auth-client.ts';

/** Mínimo que exige el servidor. Se comprueba aquí para avisar antes de enviar. */
const MIN_LENGTH = 12;

export function ResetPasswordPage({ token, linkError }: { token?: string; linkError?: string }) {
  const { t } = useTranslation();

  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const invalidLink = Boolean(linkError) || !token;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (!token) {
      return;
    }

    if (password.length < MIN_LENGTH) {
      setError(t('auth.reset.tooShort', { min: MIN_LENGTH }));
      return;
    }

    setError(null);
    setLoading(true);

    const { error: resetError } = await authClient.resetPassword({ newPassword: password, token });

    setLoading(false);

    if (resetError) {
      // Casi siempre es que el enlace caducó mientras la pestaña estaba abierta.
      setError(t('auth.reset.failed'));
      return;
    }

    setDone(true);
  }

  return (
    <AuthLayout>
      <Card title={t('auth.reset.title')}>
        {done ? (
          <>
            <Notice>{t('auth.reset.done')}</Notice>
            <Link
              to="/entrar"
              className="inline-flex w-full items-center justify-center rounded-lg bg-[var(--color-brand)] px-4 py-2.5 text-sm font-medium text-white hover:bg-[var(--color-brand-strong)]"
            >
              {t('auth.reset.goSignIn')}
            </Link>
          </>
        ) : invalidLink ? (
          <>
            <ErrorMessage>{t('auth.reset.invalidLink')}</ErrorMessage>
            <Link
              to="/recuperar"
              className="inline-flex w-full items-center justify-center rounded-lg bg-[var(--color-brand)] px-4 py-2.5 text-sm font-medium text-white hover:bg-[var(--color-brand-strong)]"
            >
              {t('auth.reset.requestAnother')}
            </Link>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <ErrorMessage>{error}</ErrorMessage>

            <Field id="password" label={t('auth.reset.newPassword')} hint={t('auth.signUp.passwordHint')}>
              <PasswordInput
                id="password"
                autoComplete="new-password"
                minLength={MIN_LENGTH}
                required
                aria-describedby="password-hint"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>

            <Button type="submit" loading={loading}>
              {loading ? t('common.loading') : t('auth.reset.submit')}
            </Button>
          </form>
        )}
      </Card>
    </AuthLayout>
  );
}
